import { Injectable } from '@angular/core'
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators'
import { ApiCourseUpdate } from './api-course-update.model';
import { ApiFilesCoursesGet } from './api-files-courses-get.model';

import { Course } from './course.model';
import { CourseSession } from './course-session.model';
import { CourseRegistration } from './course-registration.model';
import { CourseVendor } from './course-vendor.model';
import { CourseObjective } from './course-objective.model';

import { ApiCourseRegistrationUpdate } from './api-course-registration-update.model';
import { ApiCourseSessionUpdate } from './api-course-session-update.model';
import { ApiCourseVendorUpdate } from './api-course-vendor-update.model';
import { ApiCourseObjectiveUpdate } from './api-course-objective-update.model';
import { User } from './user.model';


@Injectable({
    providedIn: 'root',
})
export class ApiService {
    private authUrl = '/api/v1/auth';
    private coursesUrl = '/api/v1/courses';
    private evaluationsUrl = '/api/v1/evaluations';
    private filesUrl = `/api/v1/files`;
    private usersUrl = `/api/v1/users`;

    constructor(
        private http: HttpClient // private course: Course
    ) {}

    loadingError = false;

    // Auth API functions
    getMenu(): Observable<any> {
        const url = `${this.authUrl}/user/menu`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    getCredits(): Observable<any> {
        const url = `${this.coursesUrl}/credits`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    getRegions(): Observable<any> {
        const url = `${this.coursesUrl}/regions`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    getStructures(): Observable<any> {
        const url = `${this.coursesUrl}/structures`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    getStatus(): Observable<any> {
        const url = `${this.coursesUrl}/statuses`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    getTagsStatewide(): Observable<any> {
        const url = `${this.coursesUrl}/tags?type=statewide`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    getBudgetDiscipline(): Observable<any> {
        const url = `${this.coursesUrl}/budget-disciplines`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    getCalendarScopes(): Observable<any> {
        const url = `${this.coursesUrl}/calendar-scopes`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    getTaxonomy(type): Observable<any> {
        // return Observable.create((observer) => {
        //     observer.next(testCourseTaxonomy);
        //     observer.complete();
        // });

        let params = new HttpParams();

        if (type) {
            params = params.append(`type`, type);
        }

        const url = `${this.filesUrl}/taxonomy`;
        return this.http
            .get(url, { params: params })
            .pipe(catchError(this.handleError));
    }

    getDeliveryMethods(): Observable<any> {
        const url = `${this.coursesUrl}/delivery-methods`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    getFiles(filters): Observable<any> {
        let params = new HttpParams();

        filters = new ApiFilesCoursesGet(filters);

        let keys = Object.keys(filters);
        for (let i = 0; i < keys.length; i++) {
            let key = keys[i];
            let value = filters[key];
            if (!value) continue;
            value = key === 'region' ? `${value},0` : value;
            params = params.append(key, value);
        }

        return this.http
            .get(`${this.filesUrl}/images/courses`, { params: params })
            .pipe(catchError(this.handleError));
    }

    getCourses(filters): Observable<any> {
        let params = new HttpParams();

        if (filters.page) {
            params = params.append(`page`, filters.page);
        }

        if (filters.search && filters.search.trim()) {
            params = params.append(`search`, filters.search);
        }

        if (filters.budgetDisciplines && filters.budgetDisciplines.length > 0) {
            params = params.append(
                `discipline_id`,
                filters.budgetDisciplines.join(',')
            );
        }

        if (filters.credits && filters.credits.length > 0) {
            params = params.append(`credit_id`, filters.credits.join(','));
        }

        if (filters.endDate != null) {
            params = params.append(`end_date`, filters.endDate);
        }

        if (filters.evaluations !== null) {
            params = params.append(
                `evaluations`,
                filters.evaluations ? '1' : '0'
            );
        }

        if (filters.image !== null) {
            params = params.append(`has_image`, filters.image ? '1' : '0');
        }

        if (filters.regions && filters.regions.length > 0) {
            params = params.append(`region_id`, filters.regions.join(','));
        }

        if (filters.tagsStatewide && filters.tagsStatewide.length > 0) {
            params = params.append(`statewide_id`, filters.tagsStatewide.join(','));
        }

        if (filters.startDate != null) {
            params = params.append(`start_date`, filters.startDate);
        }

        if (filters.statuses && filters.statuses.length > 0) {
            params = params.append(`status_id`, filters.statuses.join(','));
        }

        if (filters.structures && filters.structures.length > 0) {
            params = params.append(
                `structure_id`,
                filters.structures.join(',')
            );
        }

        const url = `${this.coursesUrl}/`;
        return this.http
            .get(url, { params: params })
            .pipe(catchError(this.handleError));
    }

    getCourse(course: Course): Observable<any> {
        //  return Observable.create((observer) => {observer.next(testCourse); observer.complete();});
        const url = `${this.coursesUrl}/${course.id}`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    createCourse(data): Observable<any> {
        let apiData = new ApiCourseUpdate(data);
        return this.http
            .post(`${this.coursesUrl}/`, apiData)
            .pipe(catchError(this.handleError));
    }

    updateCourse(course:Course, data): Observable<any> {
        let apiData = new ApiCourseUpdate(data);
        return this.http
            .put(`${this.coursesUrl}/${course.id}`, apiData)
            .pipe(catchError(this.handleError));
    }

    deleteCourse(id): Observable<any> {
        const url = `${this.coursesUrl}/${id}`;
        return this.http.delete(url).pipe(catchError(this.handleError));
    }

    /** GET sessions */
    getCourseSessions(course: Course, filters:object = {}): Observable<any> {
        let params = new HttpParams();

        Object.keys(filters).forEach((key) => {
            // console.log({ key: filters[key] })
            params = params.append(key, filters[key]);
        });

        const url = `${this.coursesUrl}/${course.id}/sessions`;
        return this.http.get(url, { params: params }).pipe(catchError(this.handleError));
    }

    /** GET session */
    getCourseSession(course: Course, session: CourseSession): Observable<any> {
        const url = `${this.coursesUrl}/${course.id}/sessions/${session.id}`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    createCourseSession(course: Course, data): Observable<any> {
        let apiData = new ApiCourseSessionUpdate(data);
        return this.http
            .post(`${this.coursesUrl}/${course.id}/sessions/`, apiData)
            .pipe(catchError(this.handleError));
    }

    updateCourseSession(course: Course, data): Observable<any> {
        let apiData = new ApiCourseSessionUpdate(data);
        return this.http
            .put(`${this.coursesUrl}/${course.id}/sessions/${data.id}`, apiData)
            .pipe(catchError(this.handleError));
    }

    /** GET registrations */
    getCourseRegistrations(course: Course, filters:object = {}): Observable<any> {
        let params = new HttpParams();

        Object.keys(filters).forEach((key) => {
            // console.log({ key: filters[key] })
            params = params.append(key, filters[key]);
        });

        const url = `${this.coursesUrl}/${course.id}/registrations`;
        return this.http.get(url, { params: params }).pipe(catchError(this.handleError));
    }

    /** GET registration */
    getCourseRegistration(course: Course, registration: CourseRegistration): Observable<any> {
        const url = `${this.coursesUrl}/${course.id}/registrations/${registration.id}`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    /** GET registrant info */
    getRegistrantParsInfo(user: User): Observable<any> {
        const url = `${this.usersUrl}/user-pars-profile/${user.id}`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    createCourseRegistration(course: Course, data): Observable<any> {
        let apiData = new ApiCourseRegistrationUpdate(data);
        return this.http
            .post(`${this.coursesUrl}/${course.id}/sessions/`, apiData)
            .pipe(catchError(this.handleError));
    }

    updateCourseRegistration(course: Course, data): Observable<any> {
        let apiData = new ApiCourseRegistrationUpdate(data);
        return this.http
            .put(`${this.coursesUrl}/${course.id}/sessions/${data.id}`, apiData)
            .pipe(catchError(this.handleError));
    }

    deleteCourseRegistration(course: Course, courseRegistration: CourseRegistration): Observable<any> {
        return this.http
            .delete(`${this.coursesUrl}/${course.id}/registrations/${courseRegistration.id}`)
            .pipe(catchError(this.handleError));
    }

    /** GET Course objectives */
    getCourseObjectives(course: Course): Observable<any> {
        const url = `${this.coursesUrl}/${course.id}/objectives`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    getCourseObjective(course: Course, objective: CourseObjective): Observable<any> {
        const url = `${this.coursesUrl}/${course.id}/objectives/${objective.id}`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    updateCourseObjective(course: Course, data): Observable<any> {
        let apiData = new ApiCourseObjectiveUpdate(data);
        return this.http
            .put(`${this.coursesUrl}/${course.id}/objectives/${data.id}`, apiData)
            .pipe(catchError(this.handleError));
    }

    createCourseObjective(course: Course, data): Observable<any> {
        let apiData = new ApiCourseObjectiveUpdate(data);
        return this.http
            .post(`${this.coursesUrl}/${course.id}/objectives`, apiData)
            .pipe(catchError(this.handleError));
    }

    deleteCourseObjective(course: Course, courseObjective: CourseObjective): Observable<any> {
        return this.http
            .delete(`${this.coursesUrl}/${course.id}/objectives/${courseObjective.id}`)
            .pipe(catchError(this.handleError));
    }

    /** GET course info by id. */
    getCourseEvaluations(courseId: number): Observable<any> {
        const url = `${this.coursesUrl}/${courseId}/evaluations`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    /** GET evaluations */
    getEvaluations(filters): Observable<any> {
        let params = new HttpParams();

        if (filters.search && filters.search.trim()) {
            params = params.append(`search`, filters.search);
        }

        if (filters.credits) {
            filters.credits.forEach((credit: number) => {
                params = params.append(`credits[]`, credit.toString());
            });
        }

        if (filters.regions) {
            filters.regions.forEach((region: number) => {
                params = params.append(`regions[]`, region.toString());
            });
        }

        if (filters.structures) {
            filters.structures.forEach((structure: number) => {
                params = params.append(`structures[]`, structure.toString());
            });
        }

        const url = `${this.evaluationsUrl}/`;
        return this.http
            .get(url, { params: params })
            .pipe(catchError(this.handleError));
    }

    /** POST: evaluations */
    createEvaluation(evaluation): Observable<any> {
        const url = `${this.evaluationsUrl}/`;
        return this.http
            .post(url, evaluation)
            .pipe(catchError(this.handleError));
    }

    /** GET evaluation */
    getEvaluation(evaluationId: string): Observable<any> {
        const url = `${this.evaluationsUrl}/${evaluationId}`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    /** PUT evaluation */
    editEvaluation(evaluation, data): Observable<any> {
        const url = `${this.evaluationsUrl}/${evaluation.id}`;
        return this.http.put(url, data).pipe(catchError(this.handleError));
    }

    /** DELETE evaluation */
    deteleEvaluation(evaluationId: string): Observable<any> {
        const url = `${this.evaluationsUrl}/${evaluationId}`;
        return this.http.delete(url).pipe(catchError(this.handleError));
    }

    /** GET credits */
    getCourseCredits(courseId): Observable<any> {
        const url = `${this.coursesUrl}/${courseId}/credits`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    /** GET vendors */
    getCourseVendors(course, filters:object = {}): Observable<any> {
        let params = new HttpParams();

        Object.keys(filters).forEach((key) => {
            // console.log({ key: filters[key] })
            params = params.append(key, filters[key]);
        });

        const url = `${this.coursesUrl}/${course.id}/vendors`;
        return this.http.get(url, { params: params }).pipe(catchError(this.handleError));
    }

    /** GET course */
    getCourseVendor(course: Course, courseVendor: CourseVendor): Observable<any> {
        const url = `${this.coursesUrl}/${course.id}/vendors/${courseVendor.id}`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    createCourseVendor(course: Course, data): Observable<any> {
        let apiData = new ApiCourseVendorUpdate(data);

        // FormData() needed for file
        const formData = new FormData(); 
        for ( var key in apiData ) {
            let value = apiData[key];
            value = value && value !== null && value !== "null" ? value : "";
            value = typeof value === "object" && !(value instanceof File) ? JSON.stringify(value) : value;
            formData.append(key, value);
        }
        
        return this.http
            .post(`${this.coursesUrl}/${course.id}/vendors/`, formData)
            .pipe(catchError(this.handleError));
    }

    updateCourseVendor(course: Course, data): Observable<any> {
        let apiData = new ApiCourseVendorUpdate(data);

        // FormData() needed for file
        const formData = new FormData(); 
        for ( var key in apiData ) {
            let value = apiData[key];
            value = value && value !== null && value !== "null" ? value : "";
            value = typeof value === "object" && !(value instanceof File) ? JSON.stringify(value) : value;
            formData.append(key, value);
        }

        return this.http
            // .put(`${this.coursesUrl}/${course.id}/vendors/${data.id}`, formData) // bug with laravel
            .post(`${this.coursesUrl}/${course.id}/vendors/${data.id}`, formData)
            .pipe(catchError(this.handleError));
    }

    deleteCourseVendor(course: Course, courseVendor: CourseVendor): Observable<any> {
        return this.http
            .delete(`${this.coursesUrl}/${course.id}/vendors/${courseVendor.id}`)
            .pipe(catchError(this.handleError));
    }

    /** GET registration status */
    getRegistrationStatus(): Observable<any> {
        const url = `${this.coursesUrl}/registrations/statuses`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    /** PUT accreditation statement updates */
    updateSessionCreditStatement(
        courseId,
        sessionFeeId,
        data
    ): Observable<any> {
        const url = `${this.coursesUrl}/${courseId}/sessionfees/${sessionFeeId}`;
        return this.http.put(url, data).pipe(catchError(this.handleError));
    }

    /** GET registrations */
    getRegistrations(evaluation, olr2 = false, params): Observable<any> {
        var queryString = Object.keys(params)
            .map((key) => (params[key] ? `${key}=${params[key]}` : null))
            .join('&');
        const url = `${this.evaluationsUrl}/${evaluation.id}/${
            olr2 ? 'olr2-' : ''
        }registrations?${queryString}`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    /** GET registration */
    getRegistration(evaluation, registration, olr2 = false): Observable<any> {
        const url = `${this.evaluationsUrl}/${evaluation.id}/${
            olr2 ? 'olr2-' : ''
        }registrations/${
            olr2
                ? registration.OLR2registration_pk
                : registration.registration_pk
        }`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    createRegistrationAttendance(courseId, registrationId, olr2, data) {
        const url = `${this.coursesUrl}/${courseId}/${
            olr2 ? 'olr2-' : ''
        }registrations/${registrationId}/attendances`;
        return this.http.post(url, data).pipe(catchError(this.handleError));
    }

    deleteRegistrationAttendance(courseId, registrationId, attendanceId, olr2) {
        const url = `${this.coursesUrl}/${courseId}/${
            olr2 ? 'olr2-' : ''
        }registrations/${registrationId}/attendances/${attendanceId}`;
        return this.http.delete(url).pipe(catchError(this.handleError));
    }

    createRegistrationAttendanceCredit(
        courseId,
        registrationId,
        attendanceId,
        olr2,
        data
    ) {
        const url = `${this.coursesUrl}/${courseId}/${
            olr2 ? 'olr2-' : ''
        }registrations/${registrationId}/attendances/${attendanceId}/credits`;
        return this.http.post(url, data).pipe(catchError(this.handleError));
    }

    deleteRegistrationAttendanceCredit(
        courseId,
        registrationId,
        attendanceId,
        attendanceCreditId,
        olr2
    ) {
        const url = `${this.coursesUrl}/${courseId}/${
            olr2 ? 'olr2-' : ''
        }registrations/${registrationId}/attendances/${attendanceId}/credits/${attendanceCreditId}`;
        return this.http.delete(url).pipe(catchError(this.handleError));
    }

    /** POST registration */
    setRegistrationAttendance(
        evaluationId,
        registration,
        olr2
    ): Observable<any> {
        let data = olr2
            ? {
                  key: 'attendance_complete_log',
                  olr2_registration_id: registration.OLR2registration_pk,
              }
            : {
                  key: 'attendance_complete_log',
                  registration_id: registration.registration_pk,
              };

        const url = `${this.evaluationsUrl}/${evaluationId}/${
            olr2 ? 'olr2-' : ''
        }registrations`;
        return this.http.post(url, data).pipe(catchError(this.handleError));
    }

    /** trigger evaluate attendance  */
    sendEvaluation(
        evaluationId: number,
        send: boolean,
        olr2: boolean
    ): Observable<any> {
        const url = `${this.evaluationsUrl}/${evaluationId}/send?ignore=${
            !send ? '1' : '0'
        }&olr2=${olr2 ? '1' : '0'}`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    /** GET evaluation log records */
    getEvaluationLogs(evaluationId: string): Observable<any> {
        const url = `${this.evaluationsUrl}/${evaluationId}/log`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    deleteCertificate(registration): Observable<any> {
        const url = `${this.coursesUrl}/${registration.event_fk}/registrations/${registration.registration_pk}/certificates/0`;
        return this.http.delete(url).pipe(catchError(this.handleError));
    }

    getCertificatePreview(event_pk: number): Observable<any> {
        const url = `${this.coursesUrl}/${event_pk}/certificates/preview`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }

    /** GET pars report */
    getParsReport(data, filters:object = {}): Observable<any> {
        let params = new HttpParams();

            params = params.append(`startDate`, filters['startDate']);
            params = params.append(`endDate`, filters['endDate']);
            params = params.append(`ahec`, data.ahec_id);

        const url = `${this.coursesUrl}/pars`;
        return this.http.get(url, { params: params, responseType: "blob"}).pipe(catchError(this.handleError));
    }

    /** GET pars individual report */
    getParsIndividualReport(data, filters:object = {}): Observable<any> {
        let params = new HttpParams();

            params = params.append(`startDate`, filters['startDate']);
            params = params.append(`endDate`, filters['endDate']);
            params = params.append(`includeLicense`, data.includeLicense);
            params = params.append(`ahec`, data.ahec_id);

        const url = `${this.coursesUrl}/pars-individual`;
        return this.http.get(url, { params: params, responseType: "blob"}).pipe(catchError(this.handleError));
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            // A client-side or network error occurred. Handle it accordingly.
            console.error('An error occurred:', error.error.message);
        } else {
            console.error(
                `Backend returned code ${
                    error.status
                }, body was: ${JSON.stringify(error.error)}`
            );
        }

        let message = '';

        if (error.error.error) {
            message = error.error.error;
        } else if (error.error.message) {
            message = error.error.message;
        } else if (typeof error.error === 'object') {
            // process laravel request rules
            message = Object.keys(error.error)
                .map(function (k) {
                    return error.error[k];
                })
                .join(', ');
        } else {
            message = 'There was an error processing your request.';
        }

        return throwError(message);
    }
}
